import React from 'react';

import { useLazyIbanCodeLookupQuery } from '@/api/v1';
import { CountryType } from '@/types/country';
import { isProbablyValidIBAN } from '@/utility/iban';

type ValidatorFn = (ibanCode: string, swiftCode: string, countryCode: CountryType) => Promise<void>;

export class IBANSwiftValidationException extends Error {
  public readonly field: 'iban' | 'swiftCode';
  public readonly parameters: readonly string[];

  constructor(field: 'iban' | 'swiftCode', message: string, ...parameters: readonly string[]) {
    super(message);

    this.name = field;
    this.field = field;
    this.parameters = parameters;
  }
}

export const useIBANAndSwiftValidation = (): ValidatorFn => {
  const [ibanLookup] = useLazyIbanCodeLookupQuery();

  return React.useCallback(
    async (ibanCode: string, swiftCode: string, countryCode: CountryType): Promise<void> => {
      const sanitizedSwiftCode = swiftCode.replace('XXX', '').trim();
      const { data: { data, success } = { data: null, success: false }, isError } =
        isProbablyValidIBAN(ibanCode)
          ? await ibanLookup(ibanCode)
          : { data: { data: null, success: true }, isError: false };
      if (isError || !success) {
        throw new IBANSwiftValidationException('iban', 'IBAN_VALIDATION_INVALID_IBAN');
      }

      if (data) {
        const { swift, country } = data;
        if (swift.id !== sanitizedSwiftCode) {
          throw new IBANSwiftValidationException('swiftCode', 'INVALID_SWIFT_CODE_FOR_IBAN');
        }

        if (country.id !== countryCode) {
          throw new IBANSwiftValidationException(
            'iban',
            'IBAN_VALIDATION_INVALID_COUNTRY',
            country.id,
            countryCode,
          );
        }
      }
    },
    [ibanLookup],
  );
};
